import classNames from "classnames";
import React, { useEffect } from "react";
import ActionRenderComponent from "../components/ActionRenderComponent";
import { IDivIconInterface, IMarker, IStaticImage, MapComponent, MarkdownPreview, useEffectWithPolling } from "uxp/components";
import { IAction } from "../action-editor/ActionEditor";
import { IWDDesignModeProps } from "../common";
import { IContextData, } from "../../../hooks/useContextData";
import { hasValue, parseValue, replaceWithData, toNum } from "../../../util";
import { IContextProvider } from "../../../uxp";
import './MapUI.scss'


export interface IMapTileSource {
    tileUrl?: string;
    mode: 'tile' | 'image';
    imageUrl?: string;
    width?: string;
    height?: string;
}

export interface IAutofillMarker {
    id: string,
    autoFillX: number,
    autoFillY: number,
}

export interface IMapConfigProps {
    mapTileSource: IMapTileSource,
    xField: string, // Longitude
    yField: string, // Latitude
    markerImageField: string,
    markerTooltipTextField?: string,
    markerSize: string,
    statusField: string,
    valueField?: string,

    useValueForMarkerColor?: boolean, // Can use value or status
    colorConfig?: Array<{ from: number, to: number, color: string }>,
    statusConfig?: Array<{ status: string, color: string }>,

    onClickAction?: IAction,
    defaultMarkerColor: string,
    minZoom?: string,
    maxZoom?: string,
    initialZoom?: string,

    autoFillIdField?: string,
    autoFillMarkersConfig: Array<IAutofillMarker>,
    // Temporary field
    autoFillIndexBeingMarkedOnTheMap?: number,

    // center 
    initialX?: string,
    initialY?: string,
    showCenterMarker?: boolean,
    centerMarkerColor?: string,
    centerMarkerIcon?: string
}

export const defaultTileUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
export const defaultMapConfigs: IMapConfigProps = {
    mapTileSource: {
        mode: 'tile',
        tileUrl: defaultTileUrl
    },
    xField: '',
    yField: '',
    statusField: '',
    valueField: '',
    markerImageField: '',
    markerTooltipTextField: '',
    statusConfig: [
        { status: 'Full', color: '#02FA5A' },
        { status: 'Closed', color: '#FF0105' },
        { status: 'Has_Space', color: '#DC9064' },
        { status: 'Empty', color: '#DDDDDD' },
        { status: 'N/A', color: '#7AC783' },
    ],
    colorConfig: [],
    markerSize: '30',
    onClickAction: null,
    defaultMarkerColor: '#096ACB',
    useValueForMarkerColor: false,
    minZoom: '1',
    maxZoom: '20',
    initialZoom: '13',
    initialX: '79.82477188110353',
    initialY: '7.739873346704005',
    autoFillMarkersConfig: [],
    autoFillIndexBeingMarkedOnTheMap: null,
    showCenterMarker: false,
    centerMarkerColor: '#7AC783',
    centerMarkerIcon: ''
}

interface IMapData {
    latitude: number,
    longitude: number,
    status: string,
    markerImage: string
}
export const defaultMapData: IMapData[] = [
    {
        latitude: 7.751645779904416,
        longitude: 79.84897613525392,
        status: "Full",
        markerImage: "https://icons.iconarchive.com/icons/gartoon-team/gartoon-action/48/adjust-colors-icon.png"
    },
    {
        latitude: 7.726471302488185,
        longitude: 79.84228134155275,
        status: "Has_Space",
        markerImage: "https://icons.iconarchive.com/icons/paomedia/small-n-flat/48/cat-icon.png"
    },
    {
        latitude: 7.728172328022716,
        longitude: 79.80224060058595,
        status: "Empty",
        markerImage: "https://icons.iconarchive.com/icons/paomedia/small-n-flat/48/clock-icon.png"
    },
    {
        latitude: 7.735172328022716,
        longitude: 79.82224060058595,
        status: "Empty",
        markerImage: "https://icons.iconarchive.com/icons/papirus-team/papirus-apps/48/office-address-book-icon.png"
    },
    {
        latitude: 7.752496242741802,
        longitude: 79.81224060058595,
        status: "Has_Space",
        markerImage: "https://icons.iconarchive.com/icons/paomedia/small-n-flat/256/rss-icon.png"
    },
    {
        latitude: 7.711842200203841,
        longitude: 79.81103897094727,
        status: "Closed",
        markerImage: "https://icons.iconarchive.com/icons/gartoon-team/gartoon-action/256/draw-brush-icon.png"
    },
    {
        latitude: 7.7147340396336626,
        longitude: 79.84365463256837,
        status: "N/A",
        markerImage: "https://icons.iconarchive.com/icons/paomedia/small-n-flat/256/address-book-icon.png"
    }
]


interface IMapUIProps {
    uxpContext: IContextProvider,
    designer: IWDDesignModeProps,
    contextData: IContextData,
    mapData: any[],
    mapConfig: IMapConfigProps,
    onSelectMarkerPosition?: (lat: number, lng: number) => void,
}

export const MapUI: React.FC<IMapUIProps> = (props) => {

    const { mapData, uxpContext, designer, contextData, onSelectMarkerPosition } = props
    const mapConfig = props?.mapConfig || defaultMapConfigs

    const [actionData, setActionData] = React.useState<{ action: IAction, contextData: any }>({ action: null, contextData: {} })

    const isInDesignerMode = () => designer && (designer.mode == 'preview' || designer.mode == 'thumbnail')
    const isInThumbnailMode = () => designer && (designer.mode == 'thumbnail')

    const { mode, tileUrl, imageUrl, width, height } = { mode: 'tile', tileUrl: defaultTileUrl, imageUrl: '', width: '', height: '', ...mapConfig?.mapTileSource }

    const url = (mode == 'tile') ? (hasValue(tileUrl)) ? replaceWithData(tileUrl, contextData) : defaultTileUrl : '';
    const staticImage: IStaticImage = (mode == 'image')
        ? {
            url: replaceWithData(imageUrl || '', contextData) || '',
            width: toNum(replaceWithData(width, contextData), 1),
            height: toNum(replaceWithData(height, contextData), 1)
        }
        : null;

    const defaultMarkerColor = mapConfig?.defaultMarkerColor || '#096ACB';
    const minZoom = toNum(parseValue(mapConfig?.minZoom, contextData), -20);
    const maxZoom = toNum(parseValue(mapConfig?.maxZoom, contextData), 20);
    const initialZoom = toNum(parseValue(mapConfig?.initialZoom, contextData), 13);
    const initialX = toNum(parseValue(mapConfig?.initialX, contextData), 79.82477188110353);
    const initialY = toNum(parseValue(mapConfig?.initialY, contextData), 7.739873346704005);
    const statusField = mapConfig.statusField || 'status';
    const valueField = mapConfig.valueField || 'value';
    const autoFillIdField = mapConfig.autoFillIdField;
    const markerImageField = mapConfig.markerImageField || 'markerImage';
    const xField = mapConfig.xField || 'longitude';
    const yField = mapConfig.yField || 'latitude';
    const iconSize = toNum(parseValue(mapConfig.markerSize, contextData), 30);

    useEffect(() => {
        console.log('marker____________mapdata', mapData)
    }, [mapData])

    // this is only used for marking the locations for marker for now and only work in design mode
    function handleClickOnMap(e: any) {
        e?.preventDefault?.()
        if (!isInDesignerMode()) {
            return;
        }

        if (!!onSelectMarkerPosition) {
            onSelectMarkerPosition(e?.latlng?.lat || undefined, e?.latlng?.lng || undefined)
        }
        return;
    }

    function renderMarker(color: string, imageUrl: string, iconSize: number = 20): IDivIconInterface {
        return {
            className: 'map-icon-marker',
            html: `
            <div class="marker" style="padding: 3px; background-color:${color}; ">
                <div class="marker-icon" style="background-image:url('${imageUrl}');"></div>
            </div>
            `,
            iconSize: [iconSize, iconSize],
        };
    }

    function getMarkerColor(status?: string, value?: number) {
        if (mapConfig.useValueForMarkerColor) {
            if (!value) return defaultMarkerColor;

            let matchingValueRange = mapConfig.colorConfig.find(
                (s) => {
                    return (s?.from <= value && s?.to >= value)
                }
            )

            if (matchingValueRange && matchingValueRange.color) {
                return matchingValueRange.color
            } else {
                return defaultMarkerColor
            }
        } else {
            if (!status) return defaultMarkerColor;

            let matchingStatus = mapConfig.statusConfig.find(
                (s: any) => s?.status?.toString()?.toUpperCase() == status?.toString().toUpperCase()
            )

            if (matchingStatus && matchingStatus.color) {
                return matchingStatus.color
            } else {
                return defaultMarkerColor
            }
        }
    }

    function addCenterMarker(markers: IMarker[]) {
        if (mapConfig?.showCenterMarker) {

            const mc = parseValue(mapConfig?.centerMarkerColor || '#7AC783', contextData)
            const mi = parseValue(mapConfig?.centerMarkerIcon || '', contextData)
            const ms = toNum(parseValue((mapConfig?.markerSize).toString(), contextData), 7)

            markers.push({
                latitude: initialY,
                longitude: initialX,
                customHTMLIcon: renderMarker(mc, mi, ms),
            })
        }
    }

    function getMarkers(): IMarker[] {
        if (mapConfig?.autoFillIndexBeingMarkedOnTheMap != null) return [];

        const markers: IMarker[] = [];

        mapData?.forEach(mD => {
            const m = { ...contextData, row: mD }

            let status = mD?.[statusField]

            let value = Number(replaceWithData(valueField || '', m) || '')

            let { autoFillX, autoFillY } = mapConfig?.autoFillMarkersConfig?.find((n) => n.id?.toString() == m?.row?.[autoFillIdField]?.toString()) || {}
            let longitude = autoFillX || toNum(replaceWithData(xField, m));
            let latitude = autoFillY || toNum(replaceWithData(yField, m));

            console.log('marker____________', m, xField, yField, replaceWithData(markerImageField, m), latitude, longitude, iconSize, getMarkerColor(status, value))

            if (!longitude || !latitude) return;

            markers.push({
                longitude: longitude,
                latitude: latitude,
                customHTMLIcon: renderMarker(getMarkerColor(status, value), replaceWithData(markerImageField, m), iconSize),
                renderTooltip: {
                    content() {
                        return <span style={{}}>
                            {
                                <MarkdownPreview value={mapConfig.markerTooltipTextField ? (replaceWithData(mapConfig.markerTooltipTextField, m) || '') : (status || '')} />
                            }
                        </span>
                    },
                    direction: "top"
                },
                data: mD
            })
        })

        // Add manual markers
        if (isInDesignerMode()) {
            markers.push(...mapConfig.autoFillMarkersConfig.map((m) => {
                return {
                    longitude: m.autoFillX,
                    latitude: m.autoFillY,
                    customHTMLIcon: renderMarker("#7AC783", null, 7),
                    renderTooltip: {
                        content() {
                            return <span style={{ fontWeight: 'var(--fontWeightBlack)' }}>
                                {autoFillIdField}: {m.id}
                            </span>
                        }
                    }
                }
            }))
        }

        addCenterMarker(markers)

        return markers;
    }

    function handleClickOnMarker(e: React.MouseEvent, item: any) {
        e?.preventDefault?.()
        if (isInDesignerMode()) {
            return;
        }

        setActionData({
            action: mapConfig?.onClickAction,
            contextData: { ...contextData, row: item }
        })
    }


    return <div className={classNames("map-component-container", { "is-being-marked": mapConfig?.autoFillIndexBeingMarkedOnTheMap != null })}>
        <MapComponent
            onClick={handleClickOnMap}
            markers={getMarkers()}
            onMarkerClick={handleClickOnMarker}
            staticImage={staticImage}
            mapUrl={url}
            minZoom={minZoom}
            maxZoom={maxZoom}
            zoom={initialZoom}
            center={{ position: { longitude: initialX, latitude: initialY } }}
        />
        {
            mapConfig?.autoFillIndexBeingMarkedOnTheMap != null &&
            <div className="mark-msg">Please mark the coordinates on the map</div>
        }

        <ActionRenderComponent
            designer={designer}
            uxpContext={uxpContext}
            {...actionData}
            onClose={() => setActionData({ action: null, contextData: {} })}
            onReloadWidget={() => { }}
        />
    </div>
}